import './UbiAccountInfo.scss';
import * as Yup from 'yup';
import { FIRST_QUESTION_ID, UBISOFT_ACCOUNT } from '../../utils/constants';
import { Field, FieldProps, useFormikContext } from 'formik';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  getUser,
  selectIsFetchingUbisoftUser,
  selectUbisoftUser
} from '../../ducks/entities';
import { useDispatch, useSelector } from 'react-redux';
import { FiRefreshCcw } from 'react-icons/fi';
import { UbisoftUser } from '../../models/ubisoft';
import iconGoals from '../../assets/images/ubisoft-playtest-icon-goals-black.webp';
import messages from './messagesUbiAccountInfo';
import { selectAuth } from '../../ducks/auth';
import siteMessages from '../../pages/root/siteMessages';
import { timeInYears } from '../../utils/generic';
import { toast } from 'react-toastify';
import { useEffect } from 'react';

interface Props {
  updateSchema: (value: any) => void;
}

interface FormUbiAccountFields {
  accountName: string;
  accountEmail: string;
  accountUsername: string;
  accountAge: string | number;
}

const UbiAccountInfo = ({ updateSchema }: Props) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const isFetchingUbisoftUser = useSelector(selectIsFetchingUbisoftUser);
  const auth = useSelector(selectAuth);
  const ubisoftUser = useSelector(selectUbisoftUser);
  const { errors, setValues, values } =
    useFormikContext<FormUbiAccountFields>();
  const ubiAccountSchema = {
    accountName: Yup.string()
      .trim()
      .required(intl.formatMessage({ ...messages.ubiAccountReload }))
  };

  const setUbiAccountFormValues = async (
    ubiUser: UbisoftUser | undefined,
    validate = false
  ) => {
    if (!ubiUser) {
      return;
    }
    await setValues(
      {
        ...values,
        accountName:
          ubiUser.firstName && ubiUser.lastName
            ? `${ubiUser?.firstName} ${ubiUser?.lastName}`
            : '',
        accountEmail: ubiUser!.email,
        accountUsername: ubiUser!.username,
        accountAge: timeInYears(ubiUser?.dateOfBirth || '')
      },
      validate
    );
  };

  useEffect(() => {
    (async () => {
      await setUbiAccountFormValues(ubisoftUser, true);
      updateSchema(ubiAccountSchema);
    })();
  }, []);

  useEffect(() => {
    const handleVisibleChange = async () => {
      if (!document.hidden) {
        if (!ubiAccountSchema.accountName.isValidSync(values.accountName)) {
          await refreshUser();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibleChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibleChange);
    };
  }, [values]);

  useEffect(() => {
    (async () => {
      await setUbiAccountFormValues(ubisoftUser, true);
    })();
  }, [ubisoftUser]);

  const refreshUser = async () => {
    try {
      await dispatch(getUser(auth.userId));
    } catch {
      toast(intl.formatMessage({ ...siteMessages.updateProfileFail }));
    }
  };

  return (
    <div className="uplay">
      <div className="header" id={FIRST_QUESTION_ID}>
        <img src={iconGoals} alt="" />
        <h3>{intl.formatMessage({ ...messages.headerUplay })}</h3>
      </div>
      <div className="content">
        {isFetchingUbisoftUser ? (
          <div className="loading">
            {[...Array(4).keys()].map((i) => (
              <p key={`uplay-${i}`} />
            ))}
          </div>
        ) : (
          <div>
            <table>
              <tbody>
                <tr>
                  <td>{intl.formatMessage({ ...messages.name })}:</td>
                  <Field name="accountName">
                    {({ field }: FieldProps) => (
                      <td id={field.name}>{field.value || '-'}</td>
                    )}
                  </Field>
                </tr>
                <tr>
                  <td>{intl.formatMessage({ ...messages.email })}:</td>
                  <Field name="accountEmail">
                    {({ field }: FieldProps) => (
                      <td id={field.name}>{field.value}</td>
                    )}
                  </Field>
                </tr>
                <tr>
                  <td>{intl.formatMessage({ ...messages.username })}:</td>
                  <Field name="accountUsername">
                    {({ field }: FieldProps) => (
                      <td id={field.name}>{field.value}</td>
                    )}
                  </Field>
                </tr>
                <tr>
                  <td>{intl.formatMessage({ ...messages.age })}:</td>
                  <Field name="accountAge">
                    {({ field }: FieldProps) => (
                      <td id={field.name}>{field.value}</td>
                    )}
                  </Field>
                </tr>
              </tbody>
            </table>
          </div>
        )}
        <div className="uplay-href">
          {errors.accountName ? (
            <div className="feedback accountFeedback" id="error-accountName">
              <FormattedMessage
                {...messages.ubiAccountReload}
                values={{
                  link: (
                    <a
                      href={UBISOFT_ACCOUNT.baseURL}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {intl.formatMessage({
                        ...messages.editUplayProfileDesc2
                      })}
                    </a>
                  )
                }}
              />
            </div>
          ) : (
            <FormattedMessage
              {...messages.editUplayProfileDesc1}
              values={{
                link: (
                  <a
                    href={UBISOFT_ACCOUNT.baseURL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {intl.formatMessage({ ...messages.editUplayProfileDesc2 })}
                  </a>
                )
              }}
            />
          )}

          <FiRefreshCcw
            className="ubiAccountRefreshBtn"
            onClick={refreshUser}
          />
        </div>
      </div>
    </div>
  );
};

export default UbiAccountInfo;
